import {
  useMutation,
  UseMutationOptions,
  UseMutationResult,
  useQuery,
  UseQueryOptions,
  UseQueryResult,
} from 'react-query';
import {
  Auth,
  AuthError,
  AuthProvider,
  browserPopupRedirectResolver,
  createUserWithEmailAndPassword,
  PopupRedirectResolver,
  signInWithEmailAndPassword,
  signInWithPopup,
  signOut,
  UserCredential,
} from 'firebase/auth';

export const useSignInWithEmailAndPassword = (
  auth: Auth,
  options?: UseMutationOptions<
    UserCredential,
    AuthError,
    { email: string; password: string }
  >
): UseMutationResult<
  UserCredential,
  AuthError,
  { email: string; password: string }
> =>
  useMutation(
    ({ email, password }) => signInWithEmailAndPassword(auth, email, password),
    options
  );

export const useCreateUserWithEmailAndPassword = (
  auth: Auth,
  options?: UseMutationOptions<
    UserCredential,
    AuthError,
    { email: string; password: string }
  >
): UseMutationResult<
  UserCredential,
  AuthError,
  { email: string; password: string }
> =>
  useMutation(
    ({ email, password }) =>
      createUserWithEmailAndPassword(auth, email, password),
    options
  );

export const useSignInWithPopup = (
  auth: Auth,
  options?: UseMutationOptions<
    UserCredential,
    AuthError,
    { provider: AuthProvider; resolver?: PopupRedirectResolver }
  >
): UseMutationResult<
  UserCredential,
  AuthError,
  { provider: AuthProvider; resolver?: PopupRedirectResolver }
> =>
  useMutation<
    UserCredential,
    AuthError,
    { provider: AuthProvider; resolver?: PopupRedirectResolver }
  >(
    ({ provider }) =>
      signInWithPopup(auth, provider, browserPopupRedirectResolver),
    options
  );

export const useSignOut = (
  auth: Auth,
  options?: UseMutationOptions<void, AuthError, void>
): UseMutationResult<void, AuthError, void> =>
  useMutation(() => signOut(auth), options);

export const useGetUserRoles = (
  auth: Auth,
  options?: UseQueryOptions<unknown, AuthError, Record<string, boolean>>
): UseQueryResult<Record<string, boolean>, AuthError> =>
  useQuery({
    queryKey: 'roles',
    queryFn: () =>
      auth.currentUser
        ?.getIdTokenResult()
        .then((idTokenResult) =>
          (idTokenResult.claims.roles as string[]).reduce(
            (roles, role) => ({ ...roles, [role]: true }),
            {
              internal_user:
                auth.currentUser?.email?.includes('@shopventory.com'),
            }
          )
        ),
    enabled: !!auth.currentUser,
    ...options,
  });
